/**
 * useNavItems - Single source of truth for the app's navigation entries.
 *
 * Both navigation surfaces render the same links from the same role gates:
 *	 - Sidebar (tablet+) — the full list in a fixed left column
 *	 - MoreMenuSheet (mobile) — the same list in the bottom "More" sheet
 *
 * Before this hook the item list + role gating lived only inside Sidebar,
 * which is why mobile users never saw the Administration section at all.
 * Keeping the definitions here means a new link (or a changed role gate)
 * lands on desktop and mobile at the same time instead of drifting.
 *
 * Returns two arrays:
 *	 - mainNavItems  — day-to-day sections, some gated to admin/super_admin
 *	 - adminNavItems — the "Administration" group (Admin, CMS, API Keys)
 *
 * Role gates mirror what the backend enforces, so a link is only ever
 * shown to a user who can actually load the page behind it.
 *
 * @example
 * const { mainNavItems, adminNavItems } = useNavItems();
 */
import React from 'react';
import { useAuth } from './useAuth';
import { RecurrenceIcon } from '../components/atoms';
import { CMS_ROLES } from '../utils/constants';

/**
 * NavItem - Describes a single navigation entry.
 *
 * @property label - Display text for the nav item
 * @property href  - Route path the item navigates to
 * @property icon  - JSX element for the SVG icon rendered beside the label
 */
export interface NavItem {
	label: string;
	href: string;
	icon: React.ReactNode;
}

/**
 * Determines whether a given nav item's href matches the current pathname.
 * Uses exact match for the root path ("/") and startsWith for all others,
 * so that "/work-orders/123" still highlights the "Work Orders" item.
 *
 * Exported because every nav surface needs the identical rule — a second
 * copy in another component is how highlight behavior drifts apart.
 */
export const isNavItemActive = (href: string, pathname: string): boolean => {
	if (href === '/') {
		return pathname === '/';
	}
	return pathname.startsWith(href);
};

/** Shared icon sizing for nav rows — 20px square, color inherited from the row. */
const ICON_CLASS = 'w-20px h-20px';

export function useNavItems(): { mainNavItems: NavItem[]; adminNavItems: NavItem[] } {
	/** Auth context — provides the role-checking utility used by every gate below */
	const { hasAnyRole } = useAuth();

	/* ---------------------------------------------------------------
	 * Navigation items — main section
	 * --------------------------------------------------------------- */

	/** Base navigation links visible to all authenticated users */
	const mainNavItems: NavItem[] = [
		{
			label: 'Dashboard',
			href: '/',
			/* Grid/home icon — represents the dashboard overview */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 0 1 6 3.75h2.25A2.25 2.25 0 0 1 10.5 6v2.25a2.25 2.25 0 0 1-2.25 2.25H6a2.25 2.25 0 0 1-2.25-2.25V6ZM3.75 15.75A2.25 2.25 0 0 1 6 13.5h2.25a2.25 2.25 0 0 1 2.25 2.25V18a2.25 2.25 0 0 1-2.25 2.25H6A2.25 2.25 0 0 1 3.75 18v-2.25ZM13.5 6a2.25 2.25 0 0 1 2.25-2.25H18A2.25 2.25 0 0 1 20.25 6v2.25A2.25 2.25 0 0 1 18 10.5h-2.25a2.25 2.25 0 0 1-2.25-2.25V6ZM13.5 15.75a2.25 2.25 0 0 1 2.25-2.25H18a2.25 2.25 0 0 1 2.25 2.25V18A2.25 2.25 0 0 1 18 20.25h-2.25a2.25 2.25 0 0 1-2.25-2.25v-2.25Z" />
				</svg>
			),
		},
		{
			label: 'Work Orders',
			href: '/work-orders',
			/* Clipboard icon — represents work order documents */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
				</svg>
			),
		},
		/* Project Work Orders — parent groupings that own an hour pool.
		   Gated to admin / super_admin since project planning isn't part
		   of the technician / reviewer / manager day-to-day. */
		...(hasAnyRole(['super_admin', 'admin']) ? [{
			label: 'Project Work Orders',
			href: '/project-work-orders',
			/* Folder-with-clipboard icon — represents a project that
			   bundles multiple work orders. */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9.776c.112-.017.227-.026.344-.026h15.812c.117 0 .232.009.344.026m-16.5 0a2.25 2.25 0 0 0-1.883 2.542l.857 6a2.25 2.25 0 0 0 2.227 1.932H19.05a2.25 2.25 0 0 0 2.227-1.932l.857-6a2.25 2.25 0 0 0-1.883-2.542m-16.5 0V6A2.25 2.25 0 0 1 6 3.75h3.879a1.5 1.5 0 0 1 1.06.44l2.122 2.12a1.5 1.5 0 0 0 1.06.44H18A2.25 2.25 0 0 1 20.25 9v.776" />
				</svg>
			),
		}] : []),
		{
			label: 'Inspections',
			href: '/inspections',
			/* Checklist/checkbox icon — represents inspection checklists */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15a2.25 2.25 0 0 1 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
				</svg>
			),
		},
		/* Recurring schedules — list pages for the recurrence_series
		   table. Same admin/super_admin gate as Project Work Orders;
		   recurrence creation is itself an admin operation, so the nav
		   entries stay hidden from technicians. The two entries share the
		   RecurrenceIcon atom so the visual language is consistent with
		   the per-row badges. */
		...(hasAnyRole(['super_admin', 'admin']) ? [{
			label: 'Recurring Work Orders',
			href: '/recurring-work-orders',
			icon: <RecurrenceIcon className={ICON_CLASS} />,
		}, {
			label: 'Recurring Inspections',
			href: '/recurring-inspections',
			icon: <RecurrenceIcon className={ICON_CLASS} />,
		}] : []),
		{
			label: 'Buildings',
			href: '/buildings',
			/* Building icon — represents building/property management */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3 4.5h.008v.008H18v-.008Zm0 3h.008v.008H18v-.008Zm0 3h.008v.008H18v-.008Z" />
				</svg>
			),
		},
		{
			label: 'Calendar',
			href: '/calendar',
			/* Calendar icon — scheduled work orders + inspections */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
				</svg>
			),
		},
		{
			label: 'Map',
			href: '/map',
			/* Map pin icon — location-based scheduled events */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
					<path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z" />
				</svg>
			),
		},
	];

	/* ---------------------------------------------------------------
	 * Navigation items — administration section (role-gated)
	 * --------------------------------------------------------------- */

	/** Admin links — conditionally included based on user role */
	const adminNavItems: NavItem[] = [];

	/* Admin link — visible to super_admin, admin, and developer roles */
	if (hasAnyRole(['super_admin', 'admin', 'developer'])) {
		adminNavItems.push({
			label: 'Admin',
			href: '/admin',
			/* Shield/users icon — represents administrative access */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
				</svg>
			),
		});
	}

	/* CMS link — CMS_ROLES. Super admins get every tab; admins and
	 * developers get Checklists + Areas, matching the backend gates (the
	 * /checklists routes allow AdminOrDeveloper; photo types and business
	 * lines stay SuperAdminOnly). */
	if (hasAnyRole([...CMS_ROLES])) {
		adminNavItems.push({
			label: 'CMS',
			href: '/cms',
			/* Stack/layers icon — represents content management */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="m6.75 7.5 3 2.25-3 2.25m4.5 0h3m-9 8.25h13.5A2.25 2.25 0 0 0 21 18V6a2.25 2.25 0 0 0-2.25-2.25H5.25A2.25 2.25 0 0 0 3 6v12a2.25 2.25 0 0 0 2.25 2.25Z" />
				</svg>
			),
		});
	}

	/* API Keys link — visible to developer and super_admin roles */
	if (hasAnyRole(['super_admin', 'developer'])) {
		adminNavItems.push({
			label: 'API Keys',
			href: '/api-keys',
			/* Key icon — represents API key management */
			icon: (
				<svg className={ICON_CLASS} fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
					<path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" />
				</svg>
			),
		});
	}

	return { mainNavItems, adminNavItems };
}

export default useNavItems;
